import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { portfolioStore } from '../stores/portfolio-store';
import { StatsUI } from '../ui/stats-ui';
import { PortfolioViewType, ChartData } from '../types';

export const StatsObserver: React.FC = observer(() => {
  const [viewType, setViewType] = useState<PortfolioViewType>('overall');
  const [pnlDisplayMode, setPnlDisplayMode] = useState<'dollar' | 'percentage'>('dollar');
  const [selectedBucket, setSelectedBucket] = useState<string>('');

  const generateColors = (count: number): string[] => {
    const baseColors = [
      '#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6',
      '#06b6d4', '#84cc16', '#f97316', '#ec4899', '#6366f1',
    ];
    
    const colors = [];
    for (let i = 0; i < count; i++) {
      colors.push(baseColors[i % baseColors.length]);
    }
    return colors;
  };

  const getAssetChartData = (): ChartData[] => {
    const assets = portfolioStore.assetDisplayData;
    const colors = generateColors(assets.length);
    
    return assets.map((asset, index) => ({
      label: `${asset.name} (${asset.symbol.toUpperCase()})`,
      value: asset.marketValue,
      color: colors[index],
    }));
  };

  const getBucketChartData = (): ChartData[] => {
    const buckets = portfolioStore.bucketDisplayData;
    const colors = generateColors(buckets.length);
    
    return buckets.map((bucket, index) => ({
      label: bucket.name,
      value: bucket.value,
      color: colors[index],
    }));
  };

  const getSelectedBucketChartData = (): ChartData[] => {
    if (!selectedBucket) return [];
    
    const bucket = portfolioStore.bucketDisplayData.find(b => b.name === selectedBucket);
    if (!bucket) return [];
    
    const colors = generateColors(bucket.assets.length);
    return bucket.assets.map((asset, index) => ({
      label: `${asset.name} (${asset.symbol.toUpperCase()})`,
      value: asset.marketValue,
      color: colors[index],
    }));
  };

  const handleViewTypeChange = (newViewType: PortfolioViewType): void => {
    setViewType(newViewType);
  };

  const handlePnlDisplayModeToggle = (): void => {
    setPnlDisplayMode(pnlDisplayMode === 'dollar' ? 'percentage' : 'dollar');
  };

  const handleSelectedBucketChange = (bucket: string): void => {
    setSelectedBucket(bucket);
  };

  // Set default selected bucket when view changes to bucket
  useEffect(() => {
    if (viewType === 'bucket' && !selectedBucket && portfolioStore.bucketNames.length > 0) {
      setSelectedBucket(portfolioStore.bucketNames[0]);
    }
  }, [viewType, selectedBucket]);

  return (
    <StatsUI
      totalAssets={portfolioStore.totalAssets}
      totalPortfolioValue={portfolioStore.totalPortfolioValue}
      totalBuckets={portfolioStore.totalBuckets}
      totalPnL={portfolioStore.totalPnL}
      totalPnLPercentage={portfolioStore.totalPnLPercentage}
      bucketNames={portfolioStore.bucketNames}
      assetChartData={getAssetChartData()}
      bucketChartData={getBucketChartData()}
      selectedBucketChartData={getSelectedBucketChartData()}
      viewType={viewType}
      pnlDisplayMode={pnlDisplayMode}
      selectedBucket={selectedBucket}
      onViewTypeChange={handleViewTypeChange}
      onPnlDisplayModeToggle={handlePnlDisplayModeToggle}
      onSelectedBucketChange={handleSelectedBucketChange}
    />
  );
});
